/**
 * Pure identity logic for the public package page (/pkg/:locationId/:slug).
 *
 * A public package ALWAYS requires OTP, so the screen order is fixed
 * (identity → otp → run) — no first-screen decision like the single form. What
 * this module owns is the SAME identity-field capture the single form performs:
 * resolve the served config (absent → DEFAULT trio, mirroring an old API without
 * SI), lay out the ordered rows (configured fields + the OTP contact field,
 * deduped), validate them, and build the `submitter` payload sent with verify-otp.
 *
 * Kept React-free so it is unit-testable under node-env vitest; the .tsx page
 * stays presentational.
 */
import type { IdentityFieldDef, SubmitterInput } from './api.js';
import { IDENTITY_FIELD_CATALOG } from './identity-config.js';
import {
  resolveIdentityConfig, otpChannelKey, identityRows,
  type IdentityRow, type IdentityFieldKey,
} from './public-form-flow.js';

// Re-exported so the page imports its whole identity toolkit from one place.
export { resolveIdentityConfig, otpChannelKey, identityRows };
export type { IdentityRow, IdentityFieldKey };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Human label for a field key (falls back to the key itself). */
export function labelForKey(key: IdentityFieldKey): string {
  return IDENTITY_FIELD_CATALOG.find((o) => o.key === key)?.label ?? key;
}

/**
 * Validate the entered identity values against the rows. Returns an error
 * message (safe to show, references labels only — never echoes a value) or null
 * when everything passes. Required fields must be non-blank; email/phone are
 * shape-checked when present.
 */
export function validateIdentityValues(
  rows: readonly IdentityRow[], values: Record<string, string>,
): string | null {
  for (const row of rows) {
    const v = (values[row.key] ?? '').trim();
    if (!v) {
      if (row.required) return `Please enter your ${labelForKey(row.key).toLowerCase()}.`;
      continue;
    }
    if (row.key === 'email' && !EMAIL_RE.test(v)) return 'Please enter a valid email address.';
    if (row.key === 'phone' && v.replace(/\D/g, '').length < 10) return 'Please enter a valid phone number.';
  }
  return null;
}

/**
 * Build the `submitter` payload from the CONFIGURED fields only (the OTP contact
 * field is sent separately and is not identity unless the config includes it).
 * An empty config → `{}` — the page omits `submitter` entirely in that case, so
 * a `[]`-config package sends no identity.
 */
export function buildSubmitter(
  config: readonly IdentityFieldDef[], values: Record<string, string>,
): SubmitterInput {
  const built: SubmitterInput = {};
  for (const f of config) {
    const v = (values[f.key] ?? '').trim();
    if (v) built[f.key] = v;
  }
  return built;
}
